import KiccEasyCardApi from '@apis/easyCard/KiccEasyCardApi';
import kiccEasyCardQueries from '@apis/queries/kiccEasyCardQueries';
import useAuthenticatePin from '@hooks/native/useAuthenticatePin';
import useDeviceId from '@hooks/native/useDeviceId';
import useRegisterPin from '@hooks/native/useRegisterPin';
import useLoading from '@hooks/ui/useLoading';
import { ErrorResponse } from '@models/error';
import { useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/** 입력한 PIN으로 현재 기기를 생체인증 기기로 등록한다 */
function useSetBioDevice() {
    const { t } = useTranslation('easyCard');
    const { startLoading, stopLoading } = useLoading();
    const { pop } = useFlow();
    const deviceId = useDeviceId();
    const queryClient = useQueryClient();
    const { registerPin } = useRegisterPin({
        onError() {
            toast.error('생체인증에 실패했습니다.');
            pop();
        },
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: kiccEasyCardQueries.bioDevice._def });
            toast.success(t('pin.bio.registered'));
            pop();
        },
    });
    // 실패해서 다시 입력받을 때마다 값이 바뀐다
    const [attempt, setAttempt] = useState(0);
    const { mutate } = useMutation({
        mutationFn: KiccEasyCardApi.setBioDevice,
        onMutate() {
            startLoading();
        },
        onSuccess(_, request) {
            registerPin(request.pin);
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            const code = e.response?.data?.code;
            if (code === 'EASY_CARD_PIN_LOCKED') {
                toast.error(t('pin.bioDevice.locked'));
                pop();
                return;
            }
            toast.error(t(code === 'EASY_CARD_PIN_MISMATCH' ? 'pin.mismatch' : 'pin.bioDevice.error'));
            setAttempt((prev) => prev + 1);
        },
        onSettled() {
            stopLoading();
        },
    });

    const handleSetBioDevice = (pin: string) => {
        console.log({ deviceId });
        if (!deviceId) {
            toast.error(t('pin.bioDevice.error'));
            setAttempt((prev) => prev + 1);
            return;
        }
        mutate({ pin, deviceId });
    };

    return {
        attempt,
        handleSetBioDevice,
    };
}

export default useSetBioDevice;
